import { useState, useEffect, useCallback } from 'react';
import type { Notification } from '../types';
import { notificationRepository } from '../data/notificationRepository';

/**
 * useNotificationDetail フックの戻り値
 */
interface UseNotificationDetailReturn {
  data: Notification | null;
  isLoading: boolean;
  error: Error | null;
  markAsRead: () => void;
  refetch: () => Promise<void>;
}

/**
 * お知らせ詳細を取得するフック
 */
export const useNotificationDetail = (id: string, accountId?: string, token?: string): UseNotificationDetailReturn => {
  const [data, setData] = useState<Notification | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await notificationRepository.getById(id, accountId || '', token);
      if (result.success) {
        if (!result.data) {
          throw new Error('お知らせが見つかりません');
        }
        setData(result.data);
      } else {
        throw new Error(result.error.message);
      }
    } catch (e) {
      setError(e as Error);
    } finally {
      setIsLoading(false);
    }
  }, [id, accountId, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const markAsRead = useCallback(() => {
    // APIモードではサーバー側で既読化されるため、ローカル状態のみ更新
    // Mockモードでの状態更新はnotificationRepositoryのmarkAsReadで行う
    if (data) {
      setData({ ...data, status: 'read' });
    }
  }, [data]);

  return { data, isLoading, error, markAsRead, refetch: fetchData };
};
